import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Trans, useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import useApi from '../../../../hooks/useApi'
import useBusinessCompletedSponsoredEvents from '../../../../hooks/useBusinessCompletedSponsoredEvents'
import Icon from '../../../icon'
import EzTooltip from '../../../tooltip'
import { formatMonthYearFromParts } from '../../../../lib/dates'
import { locationDisplayText } from '../../../../lib/location'

import styles from './EventMultiplier.module.scss'

const MAX_COMPLETED_EVENT_CARDS = 3

function getImpactKey(tKey, hasOrganizer, hasSupportedCharities) {
  if (hasOrganizer && hasSupportedCharities) {
    return `${tKey}.impactWithOrganizer`
  }
  if (hasOrganizer && !hasSupportedCharities) {
    return `${tKey}.impactWithOrganizerNoCharities`
  }
  if (!hasOrganizer && hasSupportedCharities) {
    return `${tKey}.impactNoOrganizer`
  }
  return `${tKey}.impactNoOrganizerNoCharities`
}

function ImpactMessage({
  tKey,
  title,
  organizerName,
  totalVolunteers,
  totalHours,
  totalCharities,
}) {
  const hasSupportedCharities = Number(totalCharities) >= 1
  const hasOrganizer = !!organizerName
  const key = getImpactKey(tKey, hasOrganizer, hasSupportedCharities)

  return (
    <p className={styles.message}>
      <Trans
        i18nKey={key}
        values={{
          event_title: title,
          organizer_name: organizerName,
          total_volunteers: totalVolunteers,
          total_volunteered_hours: totalHours,
          total_charities_donated: totalCharities,
        }}
        components={{
          pink: <span className={styles.messageEmphasis} />,
        }}
      />
    </p>
  )
}

export default function EventMultiplier({
  businessId,
  enabled = true,
  locale = 'en',
}) {
  const { t } = useTranslation()
  const tKey = 'profiles.business.eventMultiplier'
  const { eventLike, eventUnlike } = useApi()
  const { events, loading } = useBusinessCompletedSponsoredEvents({
    businessId,
    enabled,
  })

  const [pageIndex, setPageIndex] = useState(0)
  const totalCards = events?.length ?? 0
  const pageCount = Math.ceil(totalCards / MAX_COMPLETED_EVENT_CARDS) || 0

  const [likesCountById, setLikesCountById] = useState({})
  const [likedById, setLikedById] = useState({})

  useEffect(() => {
    setPageIndex(0)

    const nextLikes = {}
    const nextLiked = {}

    ;(events ?? []).forEach(e => {
      const eventId = e.event_id ?? e.id
      if (eventId == null) return
      nextLikes[eventId] =
        typeof e.likes_count === 'number' ? e.likes_count : e.likes_count ?? 0
      if (typeof e.current_user_likes === 'boolean')
        nextLiked[eventId] = e.current_user_likes
    })

    setLikesCountById(nextLikes)
    setLikedById(nextLiked)
  }, [events])

  const visibleEvents = useMemo(() => {
    const start = pageIndex * MAX_COMPLETED_EVENT_CARDS
    return (events ?? []).slice(start, start + MAX_COMPLETED_EVENT_CARDS)
  }, [events, pageIndex])

  const prevLabel = t(`${tKey}.previous`, 'previous')
  const nextLabel = t(`${tKey}.next`, 'next')
  const viewStoryLabel = t(`${tKey}.viewStory`, 'view story')

  const handlePrev = () => setPageIndex(p => Math.max(0, p - 1))
  const handleNext = () =>
    setPageIndex(p => (pageCount > 0 ? Math.min(pageCount - 1, p + 1) : 0))

  const canToggleLikeForEvent = e => typeof e?.current_user_likes === 'boolean'

  const handleToggleLike = async e => {
    if (!canToggleLikeForEvent(e)) return

    const eventId = e.event_id ?? e.id
    if (eventId == null) return

    const currentlyLiked = !!likedById[eventId]
    const previousLikesCount = likesCountById[eventId] ?? e.likes_count ?? 0

    // Optimistic UI update
    setLikedById(prev => ({ ...prev, [eventId]: !currentlyLiked }))
    setLikesCountById(prev => ({
      ...prev,
      [eventId]: previousLikesCount + (currentlyLiked ? -1 : 1),
    }))

    try {
      if (currentlyLiked) {
        await eventUnlike({ id: eventId })
      } else {
        await eventLike({ id: eventId })
      }
    } catch (err) {
      setLikedById(prev => ({ ...prev, [eventId]: currentlyLiked }))
      setLikesCountById(prev => ({ ...prev, [eventId]: previousLikesCount }))
    }
  }

  return (
    <section className={styles.section}>
      <div className={styles.taglineRow}>
        <div className={styles.taglineText}>
          {t(`${tKey}.tagline`, 'sponsoring your community')}
        </div>
        <EzTooltip
          id="event_multiplier_tooltip"
          type="primary"
          placement="top"
          outerContent={<Icon name="info" className={styles.tooltipIcon} />}
          innerContent={t(
            `${tKey}.tooltip`,
            'Highlights of sponsor impacts in the community.',
          )}
        />
      </div>

      <div className={styles.titleRow}>
        <h2 className={styles.titleText}>
          {t(`${tKey}.title`, 'Event Multiplier')}
        </h2>
        <Icon name="heart" className={styles.headerHeart} />
      </div>

      {loading ? (
        <p className={styles.placeholder}>
          {t('profiles.loading', 'Loading...')}
        </p>
      ) : null}

      {!loading && visibleEvents.length === 0 ? (
        <p className={styles.placeholder}>
          {!enabled
            ? t(`${tKey}.signInToView`, 'Sign in to view completed events.')
            : t(`${tKey}.empty`, 'No completed events yet.')}
        </p>
      ) : null}

      <div className={styles.eventsList}>
        {visibleEvents.map((event, idx) => {
          const cause = event?.cause ?? null
          const causeLabel =
            locale === 'es'
              ? cause?.es_name ?? cause?.en_name ?? ''
              : cause?.en_name ?? cause?.es_name ?? ''
          const causeIconName = cause?.icon ?? 'heart'

          const organizerName = event?.organizer_name ?? ''
          const title = event?.event_title ?? ''
          const totalVolunteers = event?.total_volunteers ?? 0
          const totalHours = event?.total_volunteered_hours ?? 0
          const totalCharities = event?.total_charities_donated ?? 0

          const monthYear = formatMonthYearFromParts(
            event?.event_end_month,
            event?.event_end_year,
            locale,
          )
          const locationStr = locationDisplayText(event?.location)

          const hasMedia = Array.isArray(event?.media_attachments)
            ? event.media_attachments.length > 0
            : false

          const eventRouteId =
            event?.event_slug ?? event?.slug ?? event?.event_id ?? event?.id

          const eventId = event?.event_id ?? event?.id
          const likesCount = likesCountById[eventId] ?? event?.likes_count ?? 0
          const isLiked = likedById[eventId] ?? event?.current_user_likes

          const likesButtonDisabled = !canToggleLikeForEvent(event)

          return (
            <React.Fragment key={eventId ?? idx}>
              {idx > 0 ? <hr className={styles.divider} /> : null}
              <div className={styles.eventStack}>
                <Link
                  to={`/events/${eventRouteId}`}
                  className={styles.eventLinkArea}
                >
                  <div className={styles.causeRow}>
                    <div className={styles.causeIconCircle}>
                      <Icon name={causeIconName} className={styles.causeIcon} />
                    </div>
                    <div className={styles.causeMeta}>
                      <div className={styles.causeLabel}>
                        {t('profiles.cause', 'cause')}
                      </div>
                      <div className={styles.causeName}>
                        {causeLabel || (locale === 'es' ? '—' : '—')}
                      </div>
                    </div>
                  </div>

                  <div className={styles.dateRow}>{monthYear}</div>
                  <div className={styles.locationRow}>{locationStr}</div>

                  <div className={styles.messageRow}>
                    <ImpactMessage
                      tKey={tKey}
                      title={title}
                      organizerName={organizerName}
                      totalVolunteers={totalVolunteers}
                      totalHours={totalHours}
                      totalCharities={totalCharities}
                    />
                  </div>
                </Link>

                <div className={styles.bottomRowWrap}>
                  <div className={styles.bottomRow}>
                    <button
                      type="button"
                      className={styles.likeBtn}
                      onClick={() => handleToggleLike(event)}
                      disabled={likesButtonDisabled}
                      aria-pressed={!!isLiked}
                    >
                      <Icon name="heart" className={styles.likeIcon} />
                      <span className={styles.likesCount}>
                        {t(`${tKey}.likesCount`, '{{count}} likes', {
                          count: likesCount,
                        })}
                      </span>
                    </button>

                    {hasMedia ? (
                      <Link
                        to={`/events/${eventRouteId}`}
                        className={styles.storyLink}
                      >
                        {viewStoryLabel}
                      </Link>
                    ) : null}
                  </div>
                </div>
              </div>
            </React.Fragment>
          )
        })}
      </div>

      {totalCards > MAX_COMPLETED_EVENT_CARDS ? (
        <div className={styles.pager}>
          <button
            type="button"
            className={styles.pagerBtn}
            onClick={handlePrev}
            disabled={pageIndex <= 0}
          >
            <Icon name="chevron-left" />
            <span>{prevLabel}</span>
          </button>

          <div className={styles.pagerDots}>
            {Array.from({ length: Math.min(pageCount, 3) }).map((_, i) => (
              <span
                key={i}
                className={`${styles.dot} ${
                  i === Math.min(pageIndex, 2) ? styles.dotActive : ''
                }`}
              />
            ))}
          </div>

          <button
            type="button"
            className={styles.pagerBtn}
            onClick={handleNext}
            disabled={pageIndex >= pageCount - 1}
          >
            <span>{nextLabel}</span>
            <Icon name="chevron-right" />
          </button>
        </div>
      ) : null}
    </section>
  )
}

EventMultiplier.propTypes = {
  businessId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  enabled: PropTypes.bool,
  locale: PropTypes.string,
}
